import { useMemo } from 'react';
import {
  CATEGORIES,
  cents,
  errorsOnly,
  formatMoney,
  itemsTotal,
  validateReceipt,
  type Category,
  type Cents,
  type Item,
  type Receipt,
  type ValidationIssue,
} from '@bill/core';
import { Icon } from './Icon.jsx';
import { MoneyField } from './MoneyField.jsx';

const CATEGORY_LABEL: Record<Category, string> = {
  alcohol: 'Alcohol',
  drink: 'Drink',
  food: 'Food',
  meat: 'Meat',
  dessert: 'Dessert',
  tobacco: 'Tobacco',
  other: 'Other',
};

function nextItemId(items: Item[]): string {
  const taken = new Set(items.map((item) => item.id));
  let n = items.length + 1;
  while (taken.has(`i${n}`)) n += 1;
  return `i${n}`;
}

/**
 * The receipt, editable in place.
 *
 * The arithmetic checks run on every keystroke and highlight the exact field
 * that is wrong. Each error also carries a one-tap fix using the number the
 * engine already computed — retyping a value the app knows is busywork.
 */
export function ReceiptEditor({
  receipt,
  previewUrl,
  onChange,
}: {
  receipt: Receipt;
  previewUrl?: string | undefined;
  onChange(receipt: Receipt): void;
}): React.ReactElement {
  const issues = useMemo(() => validateReceipt(receipt), [receipt]);
  const errors = errorsOnly(issues);
  const byItem = new Map<string, ValidationIssue[]>();
  const receiptLevel: ValidationIssue[] = [];
  for (const issue of issues) {
    if (issue.itemId) byItem.set(issue.itemId, [...(byItem.get(issue.itemId) ?? []), issue]);
    else receiptLevel.push(issue);
  }

  const patch = (changes: Partial<Receipt>): void => onChange({ ...receipt, ...changes });
  const patchItem = (id: string, changes: Partial<Item>): void =>
    patch({ items: receipt.items.map((item) => (item.id === id ? { ...item, ...changes } : item)) });
  const removeItem = (id: string): void => patch({ items: receipt.items.filter((item) => item.id !== id) });
  const addItem = (): void =>
    patch({
      items: [
        ...receipt.items,
        { id: nextItemId(receipt.items), name: '', qty: 1, unitPrice: cents(0), lineTotal: cents(0), category: 'other' },
      ],
    });

  const { currency } = receipt;
  const lines = itemsTotal(receipt);

  return (
    <div className="editor">
      <ul className="items">
        {receipt.items.map((item) => {
          const own = byItem.get(item.id) ?? [];
          const invalid = (field: string): boolean => own.some((issue) => issue.field === field && issue.severity === 'error');
          return (
            <li key={item.id} className="item">
              <div className="item-row">
                <select
                  aria-label="Category"
                  className="category"
                  value={item.category}
                  onChange={(event) => patchItem(item.id, { category: event.target.value as Category })}
                >
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {CATEGORY_LABEL[category]}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="name"
                  aria-label="Item name"
                  placeholder="Item"
                  value={item.name}
                  onChange={(event) => patchItem(item.id, { name: event.target.value })}
                />
                <button type="button" className="remove" aria-label={`Remove ${item.name || 'item'}`} onClick={() => removeItem(item.id)}>
                  <Icon name="x" size={16} />
                </button>
              </div>
              <div className="item-row nums">
                <label>
                  <span>qty</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    className={invalid('qty') ? 'invalid' : ''}
                    value={item.qty}
                    onChange={(event) => patchItem(item.id, { qty: Number.parseInt(event.target.value, 10) || 0 })}
                  />
                </label>
                <label>
                  <span>each</span>
                  <MoneyField
                    aria-label="Unit price"
                    value={item.unitPrice}
                    invalid={invalid('unitPrice')}
                    onChange={(value) => patchItem(item.id, { unitPrice: value ?? cents(0) })}
                  />
                </label>
                <label>
                  <span>line</span>
                  <MoneyField
                    aria-label="Line total"
                    value={item.lineTotal}
                    invalid={invalid('lineTotal')}
                    onChange={(value) => patchItem(item.id, { lineTotal: value ?? cents(0) })}
                  />
                </label>
              </div>
              {own.map((issue, i) => (
                <IssueLine
                  key={i}
                  issue={issue}
                  currency={currency}
                  onFix={
                    issue.code === 'item-line-total-mismatch' && issue.expected !== undefined
                      ? () => patchItem(item.id, { lineTotal: issue.expected as Cents })
                      : undefined
                  }
                />
              ))}
            </li>
          );
        })}
      </ul>

      <button type="button" className="button ghost" onClick={addItem}>
        <Icon name="plus" size={16} /> Add a line
      </button>

      <section className="totals-edit">
        <div className="total-row muted">
          <span>Items</span>
          <span>{formatMoney(lines, currency)}</span>
        </div>
        <TotalField label="Subtotal" value={receipt.subtotal} onChange={(subtotal) => patch({ subtotal })} />
        <TotalField label="Tax" value={receipt.tax} onChange={(tax) => patch({ tax })} />
        <TotalField label="Tip" value={receipt.tip} onChange={(tip) => patch({ tip })} />
        <TotalField label="Total" value={receipt.total} strong onChange={(total) => patch({ total })} />
        <label className="total-row">
          <span>Currency</span>
          <input
            type="text"
            className="currency"
            aria-label="Currency code"
            value={currency}
            maxLength={3}
            onChange={(event) => patch({ currency: event.target.value.toUpperCase() })}
          />
        </label>
      </section>

      {receiptLevel.map((issue, i) => {
        const residual =
          issue.code === 'unexplained-residual' && issue.expected !== undefined && issue.actual !== undefined
            ? issue.actual - issue.expected
            : 0;
        return (
          <IssueLine
            key={i}
            issue={issue}
            currency={currency}
            fixLabel={
              issue.code === 'items-subtotal-mismatch'
                ? `Use ${formatMoney(lines, currency)}`
                : issue.code === 'missing-total'
                  ? 'Fill it in'
                  : residual > 0
                    ? `Treat ${formatMoney(cents(residual), currency)} as tip`
                    : undefined
            }
            onFix={
              issue.code === 'items-subtotal-mismatch'
                ? () => patch({ subtotal: lines })
                : issue.code === 'missing-total'
                  ? () => patch({ total: cents(lines + (receipt.tax ?? 0) + (receipt.tip ?? 0)) })
                  : residual > 0
                    ? () => patch({ tip: cents((receipt.tip ?? 0) + residual) })
                    : undefined
            }
          />
        );
      })}

      {previewUrl && (
        <details>
          <summary>Show the photo</summary>
          <img src={previewUrl} alt="The receipt" />
        </details>
      )}
    </div>
  );
}

function TotalField({
  label,
  value,
  strong,
  onChange,
}: {
  label: string;
  value: Cents | undefined;
  strong?: boolean;
  onChange(value: Cents | undefined): void;
}): React.ReactElement {
  return (
    <label className={`total-row${strong ? ' strong' : ''}`}>
      <span>{label}</span>
      <MoneyField aria-label={label} value={value} placeholder="—" onChange={onChange} />
    </label>
  );
}

function IssueLine({
  issue,
  currency,
  onFix,
  fixLabel,
}: {
  issue: ValidationIssue;
  currency: string;
  onFix?: (() => void) | undefined;
  fixLabel?: string | undefined;
}): React.ReactElement {
  const label =
    fixLabel ?? (issue.expected !== undefined ? `Use ${formatMoney(issue.expected, currency)}` : 'Fill it in');
  return (
    <p className={`issue ${issue.severity}`}>
      {issue.message}
      {onFix && (
        <button type="button" className="link" onClick={onFix}>
          {label}
        </button>
      )}
    </p>
  );
}
